import type { CameraController } from '../../controller/CameraController';
import type { EventHub } from '../../events/EventHub';
import type { GameState } from '../../model/gamestate/GameState';
import type { TileMapRenderer } from '../../renderer/tilemap/TileMapRenderer';
import type { SkillBar } from '../../ui/SkillBar';
import type { PlayerInventory } from '../../ui/PlayerInventory';
import type { CodexWindow } from '../../ui/CodexWindow';
import type { CodexPicker } from '../../ui/CodexPicker';
import type { ItemBar } from '../../ui/ItemBar';
import { createChatBox } from '../../ui/ChatBox';
import type { ChatBoxHandle } from '../../ui/ChatBox';
import { createIntentQueueDisplay } from '../../ui/IntentQueueDisplay';
import { createUserMenuPopup, type UserMenuPopupHandle } from '../../ui/UserMenuPopup';
import type { EntityIdCardHandle } from '../../ui/EntityIdCard';
import type { SpaceTimeClient } from '../../spacetime/SpaceTimeClient';
import { t } from '../../i18n';
import { track } from './utils';

interface SessionConfig {
  username: string;
}

interface CreateSessionSubscriptionManagerParams {
  client: SpaceTimeClient;
  config: SessionConfig;
  gameState: GameState;
  renderer: TileMapRenderer;
  cameraController: CameraController;
  eventHub: EventHub;
  hud: HTMLSpanElement | null;
  playerInventory: PlayerInventory;
  codexWindow: CodexWindow;
  codexPicker: CodexPicker;
  itemBarUtils: ItemBar;
  skillBarUtils: SkillBar | null;
  entityIdCard: EntityIdCardHandle;
  dungeons: ReadonlyArray<{ id: string; name: string }>;
  onDungeonEnter: (dungeonId: string) => Promise<void>;
  onStop?: () => void;
}

export interface SessionSubscriptionManager {
  start(): Promise<void>;
  stop(): void;
  getUserMenuPopupHandle(): UserMenuPopupHandle | null;
  getChatBoxHandle(): ChatBoxHandle | null;
}

export function createSessionSubscriptionManager(params: CreateSessionSubscriptionManagerParams): SessionSubscriptionManager {
  const {
    client,
    config,
    gameState,
    renderer,
    cameraController,
    eventHub,
    hud,
    playerInventory,
    codexWindow,
    codexPicker,
    itemBarUtils,
    skillBarUtils,
    entityIdCard,
    dungeons,
    onDungeonEnter,
    onStop,
  } = params;

  let sessionSubscriptions: Array<{ unsubscribe: () => void }> = [];
  let chatBoxHandle: ChatBoxHandle | null = null;
  let userMenuPopupHandle: UserMenuPopupHandle | null = null;
  let intentQueueDisplay: { destroy(): void } | null = null;

  const stop = (): void => {
    sessionSubscriptions.forEach((subscription) => subscription.unsubscribe());
    sessionSubscriptions = [];

    chatBoxHandle?.destroy();
    chatBoxHandle = null;

    userMenuPopupHandle?.destroy();
    userMenuPopupHandle = null;

    intentQueueDisplay?.destroy();
    intentQueueDisplay = null;

    onStop?.();
    entityIdCard.close();

    console.log('[Lifecycle] Session subscriptions stopped and components destroyed');
  };

  const start = async (): Promise<void> => {
    const [itemCatalog, mobCatalog, appearanceCatalog] = await Promise.all([
      client.catalogs.syncItemCatalog(),
      client.catalogs.syncMobCatalog(),
      client.catalogs.syncAppearanceCatalog(),
    ]);
    playerInventory.updateItemCatalog([...itemCatalog.entries]);
    codexWindow.updateItems([...itemCatalog.entries]);
    codexWindow.updateMobs([...mobCatalog.entries]);
    codexWindow.updateAppearances([...appearanceCatalog.entries]);
    codexPicker.updateItems([...itemCatalog.entries]);
    codexPicker.updateMobs([...mobCatalog.entries]);
    codexPicker.updateAppearances([...appearanceCatalog.entries]);
    itemBarUtils.updateItemCatalog([...itemCatalog.entries]);
    console.log('[Catalogs] synchronized', {
      itemsVersion: itemCatalog.version,
      itemsCount: itemCatalog.entries.length,
      mobsVersion: mobCatalog.version,
      mobsCount: mobCatalog.entries.length,
      appearancesVersion: appearanceCatalog.version,
      appearancesCount: appearanceCatalog.entries.length,
    });


    const inventorySubscription = await client.inventory.subscribeToInventory((items) => {
      playerInventory.updateItems(items);
    });
    track(sessionSubscriptions, inventorySubscription);

    const itemBarSubscription = await client.inventory.subscribeToItemBar((items) => {
      itemBarUtils.update(items);
      playerInventory.updateItemBar(items);
    });
    track(sessionSubscriptions, itemBarSubscription);

    const equipmentSubscription = await client.inventory.subscribeToEquipment((slots) => {
      playerInventory.updateEquipment(slots);
    });
    track(sessionSubscriptions, equipmentSubscription);

    const storageSubscription = await client.inventory.subscribeToStorage((snapshot) => {
      playerInventory.updateStorage(snapshot.slots);
    });
    track(sessionSubscriptions, storageSubscription);

    chatBoxHandle = createChatBox(gameState, client, eventHub);

    userMenuPopupHandle = createUserMenuPopup(client, {
      dungeons,
      onDungeonEnter,
    });

    const intentQueueRoot = document.getElementById('intent-queue-root');
    if (intentQueueRoot) {
      intentQueueDisplay = createIntentQueueDisplay(gameState, intentQueueRoot, eventHub);
    }

    const skillsSubscription = await client.skills.subscribeToSkills((skills) => {
      skillBarUtils?.updateSkills(skills);
      playerInventory.updateSkills(skills);
      codexWindow.updateSkills(skills);
      codexPicker.updateSkills(skills);
    });
    track(sessionSubscriptions, skillsSubscription);

    const skillBarSubscription = await client.skills.subscribeToSkillBar((bar) => {
      skillBarUtils?.updateSkillBar(bar?.skills ?? []);
      playerInventory.updateSkillBar(bar?.skills ?? []);
      codexWindow.updateSkillBar(bar?.skills ?? []);
      codexPicker.updateSkillBar(bar?.skills ?? []);
    });
    track(sessionSubscriptions, skillBarSubscription);

    const tickSubscription = await client.gameTick.subscribeToGameTick({
      onTickUpdate: (serverTick) => {
        gameState.setServerTick(serverTick);
        const localPos = gameState.getCharacterPosition();
        const queueSize = gameState.getPendingQueue().length;
        skillBarUtils?.render();
        if (hud) {
          hud.textContent = t('hud.tickStatus', {
            tick: serverTick,
            x: localPos.x,
            y: localPos.y,
            queue: queueSize,
          });
        }
      }
    });
    track(sessionSubscriptions, tickSubscription);


  };

  return {
    start,
    stop,
    getUserMenuPopupHandle: () => userMenuPopupHandle,
    getChatBoxHandle: () => chatBoxHandle,
  };
}
